import { HandleGalleryFns } from "../../hooks/handleGalleryFns"
import dropdownIcon from "../../assets/navbar/dropdown.svg"
import dropdownActive from "../../assets/navbar/dropdownActive.svg"
import Dropdown from "../gallery/Dropdown"
import { Tooltip } from "@mui/material"
import { MyDispatch, MySelector } from "../../redux/store"
import { useDispatch } from "react-redux"
import { setCurrentFolder, setCurrentFolderData, setAiSearchActive } from "../../redux/utilSlice"
import { getPhysicalAlbumCount } from "../../utils/getPhysicalAlbumCount"
import { getFilesInAnyAlbum } from "../../utils/getFilesInAnyAlbum"
import { getDesignLabelClass } from "../../gallery-designs/registry"

const GalleryFolderSelectionBar = ({
  currentFolder,
  setFolderAndReset,
}: {
  currentFolder: string
  setFolderAndReset: (arg: string) => void
}) => {
  const dispatch = useDispatch<MyDispatch>()

  const { setAllPhotos, setAllVideos, setFolderFiles, folders } =
    HandleGalleryFns()

  const { theme } = MySelector((state) => state.theme)
  const { navHeight, aiSearchData } = MySelector((state) => state.util)
  const { data, category, eventId } = MySelector((state) => state.event)
  const { allFiles, albumMemberships } = MySelector((state) => state.gallery)

  const filesInAnyAlbum = getFilesInAnyAlbum(albumMemberships)
  // Folder tab casing follows the active design (e.g. UPPERCASE for editorial).
  const labelClass = getDesignLabelClass(data?.galleryTheme) || "capitalize text-sm"

  const customEventId = "180f7392-4fc1-423c-8f57-fc5913d18049"

  // Function to render a folder button
  const renderFolderItem = (
    name: string,
    id?: string,
    dropdownCss?: string,
    isDropdown?: boolean
  ) => {
    const maxLength = isDropdown ? 17 : 12

    // Get physical album count for this folder (host only)
    const physicalCount = category === "host"
      ? getPhysicalAlbumCount(id || null, name, allFiles, filesInAnyAlbum, name === "All Videos")
      : 0

    return (
      <Tooltip
        key={id || name}
        title={name?.length > maxLength ? name : undefined}
        arrow
        placement="right"
      >
        <div
          className={`${labelClass} cursor-pointer text-center min-w-[100px] p-2 rounded-xl transition-all duration-100 ease-linear flex flex-col items-center gap-1 ${
            name === currentFolder
              ? "bg-newDarkBlack dark:bg-golden text-white dark:text-[#08090B]"
              : "text-newOffBlack dark:text-white hover:text-newDarkBlack dark:hover:text-golden"
          } ${dropdownCss}`}
          onClick={() => {
            dispatch(
              setCurrentFolder(
                name === "All Photos" || name === "All Videos" || name === "AI Search"
                  ? "common"
                  : "custom"
              )
            )
            setFolderAndReset(name)
            if (name === "AI Search") {
              // AI Search tab selected - will be handled by Gallery component
              dispatch(setCurrentFolderData([]))
              dispatch(setAiSearchActive(true))
            } else if (id) {
              setFolderFiles(id)
            } else if (name === "All Photos") {
              setAllPhotos()
            } else {
              setAllVideos()
            }
          }}
        >
          <span>{name?.length > maxLength ? `${name.slice(0, maxLength)}...` : name}</span>
          {category === "host" && physicalCount > 0 && (
            <span className="text-xs opacity-75 lowercase">{physicalCount} selected</span>
          )}
        </div>
      </Tooltip>
    )
  }

  const firstTwoFolders = folders?.slice(0, 2) || []
  const remainingFolders = folders?.slice(2) || []

  const isCurrentInFirstTwo = firstTwoFolders.some(
    (f: { name: string }) => f.name === currentFolder
  )
  
  // Check if current folder is one of the visible tabs (not in dropdown)
  const isCurrentInVisibleTabs = 
    isCurrentInFirstTwo ||
    currentFolder === "All Photos" ||
    currentFolder === "All Videos" ||
    currentFolder === "AI Search"
    
  const dropdownTitle = isCurrentInVisibleTabs ? "More" : currentFolder

  return (
    <div
      className="bg-white/85 dark:bg-black/85 sticky z-[505] hidden md:flex border-[0.5px] border-offGrey/50 p-1.5 items-center flex-wrap justify-center mx-10 gap-1.5 rounded-2xl md:mb-8 lg:mb-16"
      style={{
        backdropFilter: "blur(12px)",
        top: navHeight,
      }}
    >
      {data?.facePrivacy && category === "guest" ? (
        // When facePrivacy is true and user is guest, only show AI Search if enabled, otherwise show nothing
        aiSearchData.aiSearchEnabled ? renderFolderItem("AI Search") : null
      ) : (
        // Show all tabs when facePrivacy is false or user is host
        <>
          {aiSearchData.aiSearchEnabled && renderFolderItem("AI Search")}

          {firstTwoFolders.map(({ id, name }: { id: string; name: string }) =>
            renderFolderItem(name, id)
          )}

          {eventId !== customEventId && renderFolderItem("All Photos")}
          {eventId !== customEventId && renderFolderItem("All Videos")}

          {remainingFolders.length > 0 ? (
            <Dropdown
              trigger={
                <p
                  className={`${labelClass} cursor-pointer min-w-[100px] justify-center p-2 rounded-xl flex items-center gap-1.5 ${
                    !isCurrentInVisibleTabs
                      ? "bg-newDarkBlack dark:bg-golden text-white dark:text-[#08090B]"
                      : "text-newOffBlack dark:text-white"
                  }`}
                >
                  <Tooltip
                    title={dropdownTitle?.length > 12 ? dropdownTitle : undefined}
                    arrow
                  >
                    <span
                      className={`${
                        dropdownTitle === "More"
                          ? "text-newOffBlack dark:text-white"
                          : ""
                      }`}
                    >
                      {dropdownTitle?.length > 12
                        ? `${dropdownTitle.slice(0, 12)}...`
                        : dropdownTitle}
                    </span>
                  </Tooltip>
                  <img
                    src={
                      !isCurrentInFirstTwo &&
                      currentFolder !== "All Photos" &&
                      currentFolder !== "All Videos"
                        ? theme === "dark"
                          ? dropdownActive
                          : dropdownIcon
                        : theme === "dark"
                        ? dropdownIcon
                        : dropdownActive
                    }
                    alt=""
                  />
                </p>
              }
              width="170px"
            >
              {remainingFolders.map(({ id, name }: { id: string; name: string }) =>
                renderFolderItem(
                  name,
                  id,
                  `text-start px-[18px] w-auto ${
                    name === currentFolder
                      ? "text-purple dark:text-golden"
                      : "text-black dark:text-white"
                  }`,
                  true
                )
              )}
            </Dropdown>
          ) : (
            remainingFolders[0] &&
            renderFolderItem(remainingFolders[0].name, remainingFolders[0].id)
          )}
        </>
      )}
    </div>
  )
}

export default GalleryFolderSelectionBar
